"use client"

import { useState, useEffect, useRef } from "react"
import Link from "next/link"
import DirectImage from "@/components/ui/direct-image"
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet"
import CartButton from "./cart-button"
import UserNavWidget from "./user-nav-widget"
import { useUserSession } from "@/lib/context/user-session-context"
import { accountsEnabled } from "@/lib/feature-flags"
import ContactDropdown from "./contact-dropdown"
import type { SiteInfo } from "@/lib/site-info"
import ShopDropdown from "./shop-dropdown"
import FulfillmentToggle from "./fulfillment-toggle"
import { Twitter, Facebook, Instagram } from "lucide-react"
import { useCart } from "@/lib/context/cart-context"
import { usePathname } from "./usePathname"

// Store status badge component – shows OPEN/CLOSED based on ordering hours
function StoreStatusBadge() {
  const { fulfillmentType } = useCart()

  // Define ordering hours (24h format) – [openHour, closeHour]
  const pickupHours: Record<number, [number, number]> = {
    0: [12, 21], // Sunday 12PM – 9PM
    1: [8, 22],  // Monday 8AM – 10PM
    2: [8, 22],  // Tuesday
    3: [8, 22],  // Wednesday
    4: [8, 22],  // Thursday
    5: [8, 22],  // Friday
    6: [8, 22],  // Saturday
  }
  const deliveryHours: Record<number, [number, number]> = {
    0: [13, 20], // Sunday 1PM – 8PM
    1: [9, 21],  // Monday 9AM – 9PM
    2: [9, 21],  // Tuesday
    3: [9, 21],  // Wednesday
    4: [9, 21],  // Thursday
    5: [9, 21],  // Friday
    6: [9, 21],  // Saturday
  }

  // Get current date/time in America/New_York regardless of visitor location
  const eastNow = new Date(
    new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      hour12: false,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).format(new Date())
  )
  const day = eastNow.getDay()
  const hoursConfig = fulfillmentType === "delivery" ? deliveryHours : pickupHours
  const [openHour, closeHour] = hoursConfig[day]
  const currentHour = eastNow.getHours() + eastNow.getMinutes() / 60
  const isOpen = currentHour >= openHour && currentHour < closeHour

  const bgClass = isOpen ? "bg-primary_green" : "bg-gray-500"
  const dotClass = isOpen ? "bg-green-300 animate-pulse" : "bg-gray-300"
  const label = isOpen ? "OPEN" : "CLOSED"
  let subLabel: string | null = null

  if (!isOpen) {
    const minutesUntilOpen = Math.round((openHour - currentHour) * 60)
    if (minutesUntilOpen > 0 && minutesUntilOpen <= 30) {
      subLabel = `Opening in ${minutesUntilOpen} min`
    } else {
      subLabel = "Pre-order Available"
    }
  }

  return (
    <div className="xl:hidden absolute left-4 top-1/2 transform -translate-y-1/2 flex flex-col items-start">
      <div className={`flex items-center ${bgClass} text-white px-2 py-1 rounded-full text-xs font-medium shadow-sm`}>
        <div className={`h-2 w-2 ${dotClass} rounded-full mr-1.5`}></div>
        <span>{label}</span>
      </div>
      {subLabel && (
        <span className="mt-0.5 text-[10px] text-gray-600 leading-none">{subLabel}</span>
      )}
    </div>
  )
}

interface NavbarProps { site: SiteInfo; embedFallback?: boolean }

export default function Navbar({ site, embedFallback = false }: NavbarProps) {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const pathname = usePathname() || '';
  
  useEffect(() => {
    // Binary: full header at the very top, collapsed the moment you scroll.
    // This only stays bounce-free because the embedded menu page disables the
    // browser's scroll anchoring (overflow-anchor: none) — otherwise the 112px
    // the header sheds would yank scrollY back across the threshold and
    // flip-flop. rAF-throttled so we don't thrash on every scroll tick.
    let ticking = false
    const handleScroll = () => {
      if (ticking) return
      ticking = true
      requestAnimationFrame(() => {
        setIsScrolled(window.scrollY > 4)
        ticking = false
      })
    }

    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false)
  }, [pathname])
  
  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen)
  }

  // Helper for active nav link
  const navLinkClass = (href: string, group?: boolean) => {
    const isActive = href === "/" ? pathname === "/" : pathname.startsWith(href);
    return (
      "text-gray-700 font-bold text-base tracking-wide transition-colors duration-200 border-b-2 py-1 whitespace-nowrap " +
      (group
        ? `group-hover:text-green-600 group-hover:border-green-600 `
        : `hover:text-green-600 hover:border-green-600 `) +
      (isActive ? "border-primary_green text-primary_green" : "border-transparent")
    );
  };

  // Mobile nav links as a component
  function MobileAccountLinks({
    setIsMobileMenuOpen,
    pathname,
  }: {
    setIsMobileMenuOpen: (v: boolean) => void
    pathname: string
  }) {
    // Accounts still WIP — hidden entirely unless flag is on for this env.
    if (!accountsEnabled()) return null;
    const { user, logout } = useUserSession()
    const isActive = pathname.startsWith("/account")
    if (!user) {
      return (
        <div className="mt-4 pt-6 border-t border-gray-200 space-y-4">
          <Link
            href="/account"
            onClick={() => setIsMobileMenuOpen(false)}
            className="flex items-center gap-2 text-lg text-primary_green font-semibold"
          >
            Sign in
          </Link>
          <Link
            href="/register"
            onClick={() => setIsMobileMenuOpen(false)}
            className="flex items-center gap-2 text-base text-gray-600"
          >
            Create an account
          </Link>
        </div>
      )
    }
    const firstName = (user.name || "").split(" ")[0] || user.name
    return (
      <div className="mt-4 pt-6 border-t border-gray-200 space-y-4">
        <p className="text-xs uppercase tracking-widest text-primary_green font-semibold">Signed in</p>
        <p className="text-base text-gray-900 -mt-2">Hi, {firstName}</p>
        <Link
          href="/account"
          onClick={() => setIsMobileMenuOpen(false)}
          className={`flex items-center gap-2 text-lg ${isActive ? "text-primary_green font-semibold" : "text-gray-700"}`}
        >
          My account
        </Link>
        <Link
          href="/account/orders"
          onClick={() => setIsMobileMenuOpen(false)}
          className="flex items-center gap-2 text-base text-gray-700"
        >
          My orders
        </Link>
        <button
          onClick={async () => { await logout(); setIsMobileMenuOpen(false) }}
          className="flex items-center gap-2 text-base text-red-600"
        >
          Sign out
        </button>
      </div>
    )
  }

  function MobileNavLinks({ setIsMobileMenuOpen }: { setIsMobileMenuOpen: (v: boolean) => void }) {
    const mobileNavLinkClass = (href: string) => {
      const isActive = href === "/" ? pathname === "/" : pathname.startsWith(href);
      return `relative py-2 flex items-center text-lg ${isActive ? "text-primary_green font-semibold" : "text-gray-700"}`;
    };
    
    return (
      <>
        <Link href="/" className={mobileNavLinkClass("/")} onClick={() => setIsMobileMenuOpen(false)}>
          <span>Home</span>
          {pathname === "/" && <span className="absolute left-[-15px] top-1/2 -translate-y-1/2 w-2 h-2 bg-primary_green rounded-full"></span>}
        </Link>
        <Link href="/shop" className={mobileNavLinkClass("/shop")} onClick={() => setIsMobileMenuOpen(false)}>
          <span>Shop</span>
          {pathname.startsWith("/shop") && <span className="absolute left-[-15px] top-1/2 -translate-y-1/2 w-2 h-2 bg-primary_green rounded-full"></span>}
        </Link>
        <Link href="/specials" className={`${mobileNavLinkClass("/specials")} text-primary_green font-semibold`} onClick={() => setIsMobileMenuOpen(false)}>
          <span>Specials</span>
          {pathname.startsWith("/specials") && <span className="absolute left-[-15px] top-1/2 -translate-y-1/2 w-2 h-2 bg-primary_green rounded-full"></span>}
        </Link>
        <Link href="/faq" className={mobileNavLinkClass("/faq")} onClick={() => setIsMobileMenuOpen(false)}>
          <span>FAQ</span>
          {pathname.startsWith("/faq") && <span className="absolute left-[-15px] top-1/2 -translate-y-1/2 w-2 h-2 bg-primary_green rounded-full"></span>}
        </Link>
        <Link href="/about" className={mobileNavLinkClass("/about")} onClick={() => setIsMobileMenuOpen(false)}>
          <span>About Us</span>
          {pathname.startsWith("/about") && <span className="absolute left-[-15px] top-1/2 -translate-y-1/2 w-2 h-2 bg-primary_green rounded-full"></span>}
        </Link>
        <Link href="/blog" className={mobileNavLinkClass("/blog")} onClick={() => setIsMobileMenuOpen(false)}>
          <span>Blog</span>
          {pathname.startsWith("/blog") && <span className="absolute left-[-15px] top-1/2 -translate-y-1/2 w-2 h-2 bg-primary_green rounded-full"></span>}
        </Link>
        <Link href="/contact" className={mobileNavLinkClass("/contact")} onClick={() => setIsMobileMenuOpen(false)}>
          <span>Contact</span>
          {pathname.startsWith("/contact") && <span className="absolute left-[-15px] top-1/2 -translate-y-1/2 w-2 h-2 bg-primary_green rounded-full"></span>}
        </Link>

        {/* Sign-in / account access at the bottom of the mobile drawer,
            visually separated with a divider so it doesn't compete with
            primary nav links. */}
        <MobileAccountLinks setIsMobileMenuOpen={setIsMobileMenuOpen} pathname={pathname} />
      </>
    );
  }

  // Check if we're on a shop page - navbar should not be sticky there
  const isShopPage = pathname.startsWith('/shop') || pathname === '/specials' || pathname.startsWith('/specials/');
  // The embedded menu (/menu) handles its own fulfillment + cart inside the
  // Dutchie iframe, so hide our Pickup/Delivery toggle and cart there.
  const isEmbedMenu = pathname === '/menu';
  // On the embedded menu, collapse the tall branded header into a thin sticky
  // bar once the user scrolls, so the Dutchie iframe gets the full viewport.
  const shrinkNav = isEmbedMenu && isScrolled;

  return (
    <header className={`${isShopPage ? 'relative' : 'sticky top-0'} z-50 bg-white shadow-sm border-b border-gray-100`}>
      <div className="container mx-auto px-4 xl:px-16">
        <div className="flex flex-col">
          {/* Main navigation row with logo in center */}
          <div className={`flex justify-center items-center relative transition-all duration-300 ${shrinkNav ? 'h-16 xl:h-16' : 'h-44 xl:h-48'}`}>
            {/* Store status badge (mobile only) */}
            <StoreStatusBadge />
            
            {/* Mobile menu - Right of logo */}
            <div className="xl:hidden absolute right-4 top-1/2 transform -translate-y-1/2">
              {/* Menu trigger button */}
              <button 
                className="p-2 text-gray-700 hover:text-green-700 focus:outline-none" 
                onClick={toggleMobileMenu}
                aria-label="Toggle mobile menu"
                aria-expanded={isMobileMenuOpen}
              >
                <div className="w-6 h-5 relative">
                  <span className={`absolute w-6 h-0.5 bg-current transition-all duration-200 ${isMobileMenuOpen ? 'rotate-45 top-2' : 'top-0'}`}></span>
                  <span className={`absolute w-6 h-0.5 bg-current transition-all duration-200 top-2 ${isMobileMenuOpen ? 'opacity-0' : 'opacity-100'}`}></span>
                  <span className={`absolute w-6 h-0.5 bg-current transition-all duration-200 ${isMobileMenuOpen ? '-rotate-45 top-2' : 'top-4'}`}></span>
                </div>
              </button>
              
              {/* Sheet drawer */}
              <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
                <SheetContent side="right" className="w-[300px] sm:w-[350px] p-0 bg-white z-50 overflow-auto">
                  {/* Drawer content */}
                  <div className="flex flex-col h-full">
                    {/* Logo header */}
                    <div className="p-4 border-b">
                      <div className="flex items-center justify-between">
                        <button 
                          onClick={() => setIsMobileMenuOpen(false)} 
                          className="p-2 -mr-2 text-gray-500 hover:text-gray-700"
                          aria-label="Close menu"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                        <div className="flex-1 flex justify-center">
                          <DirectImage
                            src="/images/716-cannabis-logo-new.png"
                            alt="716 Cannabis Logo"
                            width={120}
                            height={48}
                            className="h-12 w-24"
                            imgClassName="h-12 w-24 object-contain"
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* Navigation links */}
                    <div className="flex-1 overflow-auto py-8 px-6">
                      <nav className="flex flex-col space-y-6 text-lg font-medium">
                        <MobileNavLinks setIsMobileMenuOpen={setIsMobileMenuOpen} />
                      </nav>
                    </div>
                    
                    {/* Social media footer */}
                    <div className="p-6 border-t mt-auto">
                      <div className="flex justify-center space-x-6">
                        <a href="https://www.facebook.com/p/716-Cannabis-100083903973292/" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-primary_green transition-colors">
                          <Facebook size={28} strokeWidth={1.5} />
                        </a>
                        <a href="https://www.instagram.com/716cannabisco/#" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-primary_green transition-colors">
                          <Instagram size={28} strokeWidth={1.5} />
                        </a>
                        <a href="https://twitter.com/716cannabisllc" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-primary_green transition-colors">
                          <Twitter size={28} strokeWidth={1.5} />
                        </a>
                      </div>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
            
            {/* Desktop Navigation - Left */}
            <div className="hidden xl:flex items-center justify-end flex-1 pr-8">
              <div className="flex items-center space-x-10">
                <Link href="/" className={navLinkClass("/")}>HOME</Link>
                <div className="relative">
                  <Link 
                    href="/shop" 
                    className={navLinkClass("/shop", true)}
                    onMouseEnter={(e) => {
                      const dropdown = e.currentTarget.nextElementSibling?.querySelector('[data-dropdown]') as HTMLElement;
                      if (dropdown) {
                        dropdown.style.opacity = '1';
                        dropdown.style.visibility = 'visible';
                      }
                    }}
                    onMouseLeave={(e) => {
                      const target = e.currentTarget;
                      setTimeout(() => {
                        const dropdown = target?.nextElementSibling?.querySelector('[data-dropdown]') as HTMLElement;
                        if (dropdown && !dropdown.matches(':hover')) {
                          dropdown.style.opacity = '0';
                          dropdown.style.visibility = 'hidden';
                        }
                      }, 150);
                    }}
                  >
                    SHOP
                  </Link>
                  <div data-dropdown>
                    <ShopDropdown />
                  </div>
                </div>
                <Link href="/faq" className={navLinkClass("/faq")}>FAQ</Link>
                <Link 
                  href="/specials" 
                  className="text-primary_green font-extrabold text-base tracking-wide transition-colors duration-200 border-b-2 py-1 hover:text-green-700 hover:border-primary_green border-transparent"
                >
                  SPECIALS
                </Link>
              </div>
            </div>

            {/* Centered Logo with mobile-specific elements */}
            <div className="z-10 px-6 flex flex-col items-center">
              <Link href="/" className="block">
                <div className="transition-transform hover:scale-105">
                  <DirectImage
                    src="/images/716-cannabis-logo-new.png"
                    alt="716 Cannabis Logo"
                    /* Size via props (not class) because DirectImage sets an
                       inline height that would override Tailwind. Shrink to a
                       ~48px logo so it fits the collapsed bar. */
                    width={shrinkNav ? 120 : 320}
                    height={shrinkNav ? 48 : 128}
                    priority={true}
                    className="transition-all duration-300"
                    imgClassName="object-contain transition-all duration-300"
                  />
                </div>
              </Link>
              
              {/* Pickup/Delivery toggle and cart button - hidden on shop pages
                  (mini menu has it), on the embedded /menu, and site-wide while
                  in api-down fallback (our fulfillment is bypassed by the embed). */}
              {!isShopPage && !isEmbedMenu && !embedFallback && (
                <div className="mt-2 flex items-center gap-3 xl:justify-center">
                  <FulfillmentToggle />
                  {/* Mobile only - Cart button to the right of fulfillment toggle */}
                  <div className="xl:hidden">
                    <CartButton showFulfillmentToggle={false} />
                  </div>
                </div>
              )}
            </div>

            {/* Desktop Navigation - Right: nav links on the left of this
                column, social+user+cart on the right, sharing one flex row
                so the cluster never overlaps CONTACT. */}
            <div className="hidden xl:flex items-center justify-between flex-1 pl-8 gap-6">
              <div className="flex items-center space-x-10">
                <Link href="/about" className={navLinkClass("/about")}>ABOUT US</Link>
                <Link href="/blog" className={navLinkClass("/blog")}>BLOG</Link>
                <div className="relative group">
                  <Link href="/contact" className={navLinkClass("/contact", true)}>CONTACT</Link>
                  {/* Invisible bridge to maintain hover state */}
                  <div className="absolute h-6 w-full -bottom-6"></div>
                  <ContactDropdown site={site} />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className={`flex items-center gap-3 ${!isShopPage ? 'pr-3 border-r border-gray-200' : ''}`}>
                  <a
                    href="https://www.facebook.com/p/716-Cannabis-100083903973292/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary_green hover:text-darkest_green transition-colors"
                    aria-label="Facebook"
                  >
                    <Facebook size={20} strokeWidth={2.5} fill="currentColor" />
                  </a>
                  <a
                    href="https://www.instagram.com/716cannabisco/#"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary_green hover:text-darkest_green transition-colors"
                    aria-label="Instagram"
                  >
                    <Instagram size={20} strokeWidth={2.5} />
                  </a>
                </div>
                <UserNavWidget />
                {!isShopPage && !isEmbedMenu && !embedFallback && <CartButton showFulfillmentToggle={false} />}
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  )
}
